'use client';

import { Skeleton } from '@mui/material';
import dayjs from 'dayjs';
import { useRouter } from 'next/navigation';
import { useState } from 'react';

import {
  useGetNotificationInboxQuery,
  useMarkAllNotificationsReadMutation,
  useMarkNotificationReadMutation,
} from '@/features/notifications/api/notificationsApi';
import {
  getNotificationHref,
  renderNotificationMessage,
} from '@/features/notifications/renderers/notificationRenderers';
import { CloseIcon, Drawer, EmptyState, InlineButton } from '@web/ui';
import { getErrorMessage, notify } from '@web/utils';

import { NotificationCard } from './NotificationCard';
import styles from './NotificationsDrawer.module.scss';

import type { NotificationInboxItem } from '@/features/notifications/types';

const INITIAL_PAGE_SIZE = 20;
const PAGE_SIZE_INCREMENT = 20;
const INITIAL_SKELETON_KEYS = ['first', 'second', 'third'];

function formatNotificationTime(value: string) {
  const timestamp = dayjs(value);
  const now = dayjs();
  const minutes = now.diff(timestamp, 'minute');
  const hours = now.diff(timestamp, 'hour');
  const days = now.diff(timestamp, 'day');

  if (minutes < 1) {
    return 'Just now';
  }

  if (minutes < 60) {
    return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
  }

  if (hours < 24) {
    return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  }

  if (days < 7) {
    return `${days} day${days === 1 ? '' : 's'} ago`;
  }

  return timestamp.format('MMM D, YYYY');
}

function normalizeNotificationHref(href: string | null) {
  if (!href) {
    return null;
  }

  try {
    const url = new URL(href, window.location.origin);
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return href;
  }
}

export function NotificationsDrawer({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const router = useRouter();
  const [pageSize, setPageSize] = useState(INITIAL_PAGE_SIZE);
  const { data, isFetching } = useGetNotificationInboxQuery(
    { page_size: pageSize },
    { skip: !open },
  );
  const [markRead] = useMarkNotificationReadMutation();
  const [markAllRead, markAllReadState] = useMarkAllNotificationsReadMutation();
  const notifications = data?.data?.results ?? [];
  const unreadCount = data?.data?.unread_count ?? 0;
  const hasMore = notifications.length < (data?.data?.count ?? 0);
  const shouldShowActions = Boolean(notifications.length);

  const handleListScroll = (event: React.UIEvent<HTMLDivElement>) => {
    if (!hasMore || isFetching) {
      return;
    }

    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    const remainingDistance = scrollHeight - scrollTop - clientHeight;

    if (remainingDistance <= 96) {
      setPageSize((current) => current + PAGE_SIZE_INCREMENT);
    }
  };

  const handleNotificationClick = async (
    notification: NotificationInboxItem,
  ) => {
    try {
      if (!notification.is_read) {
        await markRead(notification.id).unwrap();
      }

      const href = normalizeNotificationHref(getNotificationHref(notification));
      if (href) {
        router.push(href);
        onClose();
      }
    } catch (error) {
      notify({ type: 'error', message: getErrorMessage(error) });
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await markAllRead().unwrap();
    } catch (error) {
      notify({ type: 'error', message: getErrorMessage(error) });
    }
  };

  const handleClose = () => {
    setPageSize(INITIAL_PAGE_SIZE);
    onClose();
  };

  return (
    <Drawer
      open={open}
      onClose={handleClose}
      width="min(400px, 92vw)"
      paperClassName={styles.notificationsDrawer}
    >
      <header className={styles.notificationsDrawer__header}>
        <div>
          <h2>Notifications</h2>
          {unreadCount ? <p>{unreadCount} unread</p> : null}
        </div>
        <button
          type="button"
          className={styles.notificationsDrawer__closeButton}
          aria-label="Close notifications"
          onClick={handleClose}
        >
          <CloseIcon size={18} />
        </button>
      </header>

      {shouldShowActions ? (
        <div className={styles.notificationsDrawer__actions}>
          <InlineButton
            ariaLabel="Mark all notifications as read"
            onClick={handleMarkAllRead}
            disabled={!unreadCount || markAllReadState.isLoading}
            isLoading={markAllReadState.isLoading}
          >
            Mark all read
          </InlineButton>
        </div>
      ) : null}

      <div
        className={styles.notificationsDrawer__list}
        onScroll={handleListScroll}
      >
        {notifications.map((notification) => (
          <NotificationCard
            key={notification.id}
            notification={notification}
            message={renderNotificationMessage(notification)}
            timestamp={formatNotificationTime(notification.event.occurred_at)}
            onClick={() => handleNotificationClick(notification)}
          />
        ))}

        {!notifications.length && !isFetching ? (
          <EmptyState
            title="You're all caught up!"
            description="Updates that need your attention will appear here."
          />
        ) : null}

        {isFetching && !notifications.length ? (
          <div
            className={styles.notificationsDrawer__skeletonList}
            aria-label="Loading notifications"
          >
            {INITIAL_SKELETON_KEYS.map((key) => (
              <div
                key={key}
                className={styles.notificationsDrawer__skeletonCard}
              >
                <Skeleton variant="text" width="58%" height={22} />
                <Skeleton variant="text" width="92%" height={18} />
                <Skeleton variant="text" width="42%" height={18} />
              </div>
            ))}
          </div>
        ) : null}

        {isFetching && notifications.length ? (
          <div className={styles.notificationsDrawer__loadingMore}>
            Loading more...
          </div>
        ) : null}
      </div>
    </Drawer>
  );
}
